import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  const verifySession = useCallback(async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      localStorage.removeItem('session_token');
      setSessionToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for existing session on mount
    const token = localStorage.getItem('session_token');
    if (token) {
      setSessionToken(token);
      verifySession(token);
    } else {
      setLoading(false);
    }
  }, [verifySession]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });
      
      if (response.data.success) {
        const token = response.data.session_token;
        localStorage.setItem('session_token', token);
        setSessionToken(token);
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        name,
        email,
        password
      });
      
      if (response.data.success) {
        const token = response.data.session_token;
        localStorage.setItem('session_token', token);
        setSessionToken(token);
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      setSessionToken(null);
      setUser(null);
    }
  };

  const setSession = (token, userObj) => {
    localStorage.setItem('session_token', token);
    setSessionToken(token);
    setUser(userObj);
  };

  const value = {
    user,
    loading,
    sessionToken,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    setSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
